import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { LoadingState } from '@srichaitra/ui';
import { labels, routes } from '@srichaitra/constants';
import { selectAuthStatus, useAppSelector } from '@srichaitra/store';

/**
 * Route guards.
 *
 * Both of them wait while the status is 'idle' or 'restoring'. That pause is
 * the important part: a stored token is verified asynchronously at start-up,
 * and redirecting before the answer arrives would bounce an already-signed-in
 * user to the login screen on every refresh.
 *
 * These control *navigation*, not authorisation. The server decides what an
 * account may actually do — a guard here is a courtesy that keeps someone from
 * landing on a page that would only fail.
 */

function isSettling(status) {
  return status === 'idle' || status === 'restoring';
}

/** Wraps the signed-in area. Sends a visitor to the login screen. */
export function RequireAuth() {
  const status = useAppSelector(selectAuthStatus);
  const location = useLocation();

  if (isSettling(status)) return <LoadingState message={labels.common.loading} />;

  if (status !== 'signedIn') {
    // `state.from` is remembered so signing in returns the user to the page
    // they actually asked for, and `replace` keeps the guarded URL out of the
    // history stack — otherwise Back lands on it again and bounces straight out.
    return <Navigate to={routes.login} replace state={{ from: location }} />;
  }

  return <Outlet />;
}

/** Wraps the signed-out area, so an authenticated user never sees the login form. */
export function RedirectIfSignedIn() {
  const status = useAppSelector(selectAuthStatus);
  const location = useLocation();

  if (isSettling(status)) return <LoadingState message={labels.common.loading} />;

  if (status === 'signedIn') {
    // RequireAuth stored the page the user was originally after; signing in
    // returns them to it rather than dropping everyone on the dashboard.
    const intended = location.state?.from?.pathname;
    return <Navigate to={intended || routes.dashboard} replace />;
  }

  return <Outlet />;
}
